// src/pages/admin/Settings.js
import React, { useState } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  useTheme,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs
} from "@mui/material";
import {
  Save as SaveIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Payment as PaymentIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Language as LanguageIcon,
  Palette as PaletteIcon,
} from "@mui/icons-material";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SettingsSection = ({ title, subtitle, children }) => (
  <Card sx={{ mb: 3 }}>
    <CardContent>
      <Typography variant="h6" fontWeight="600" gutterBottom>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>
      )}
      {children}
    </CardContent>
  </Card>
);

export default function Settings() {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [settings, setSettings] = useState({
    // General Settings
    companyName: "FreshSweeper Cleaning",
    companyEmail: "admin@freshsweeper.com",
    companyPhone: "+1 (555) 123-4567",
    timezone: "America/New_York",
    currency: "USD",
    
    // Notification Settings
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    bookingAlerts: true,
    paymentAlerts: true,
    reviewAlerts: true,
    
    // Security Settings
    twoFactorAuth: false,
    sessionTimeout: 30,
    passwordExpiry: 90,
    
    // Payment Settings
    stripeEnabled: true,
    paypalEnabled: true,
    taxRate: 8.875,
    autoPayout: true,
    payoutSchedule: "weekly",
  });

  const [changePassword, setChangePassword] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveSettings = () => {
    // Here you would save settings to your backend
    console.log('Saving settings:', settings);
  };

  const notificationSettings = [
    { label: 'Email Notifications', key: 'emailNotifications', description: 'Receive important updates via email' },
    { label: 'SMS Notifications', key: 'smsNotifications', description: 'Get text message alerts' },
    { label: 'Push Notifications', key: 'pushNotifications', description: 'Browser and mobile push notifications' },
    { label: 'Booking Alerts', key: 'bookingAlerts', description: 'Notify when new bookings are made' },
    { label: 'Payment Alerts', key: 'paymentAlerts', description: 'Alert for successful payments' },
    { label: 'Review Alerts', key: 'reviewAlerts', description: 'Notify when customers leave reviews' },
  ];

  const securitySettings = [
    { label: 'Two-Factor Authentication', key: 'twoFactorAuth', description: 'Add an extra layer of security to your account' },
  ];

  const paymentSettings = [
    { label: 'Stripe Payments', key: 'stripeEnabled', description: 'Accept credit card payments via Stripe' },
    { label: 'PayPal Payments', key: 'paypalEnabled', description: 'Accept payments via PayPal' },
    { label: 'Auto Payouts', key: 'autoPayout', description: 'Automatically process cleaner payouts' },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your application settings and preferences
        </Typography>
      </Box>

      {/* Tabs */}
      <Card>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<BusinessIcon />} label="General" />
          <Tab icon={<NotificationsIcon />} label="Notifications" />
          <Tab icon={<SecurityIcon />} label="Security" />
          <Tab icon={<PaymentIcon />} label="Payments" />
          <Tab icon={<PeopleIcon />} label="Team" />
        </Tabs>

        {/* General Settings */}
        <TabPanel value={tabValue} index={0}>
          <SettingsSection title="Company Information" subtitle="Basic information about your business">
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Company Name"
                  value={settings.companyName}
                  onChange={(e) => handleSettingChange('companyName', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Contact Email"
                  value={settings.companyEmail}
                  onChange={(e) => handleSettingChange('companyEmail', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Phone Number"
                  value={settings.companyPhone}
                  onChange={(e) => handleSettingChange('companyPhone', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Timezone</InputLabel>
                  <Select
                    value={settings.timezone}
                    label="Timezone"
                    onChange={(e) => handleSettingChange('timezone', e.target.value)}
                  >
                    <MenuItem value="America/New_York">Eastern Time (ET)</MenuItem>
                    <MenuItem value="America/Chicago">Central Time (CT)</MenuItem>
                    <MenuItem value="America/Denver">Mountain Time (MT)</MenuItem>
                    <MenuItem value="America/Los_Angeles">Pacific Time (PT)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={settings.currency}
                    label="Currency"
                    onChange={(e) => handleSettingChange('currency', e.target.value)}
                  >
                    <MenuItem value="USD">US Dollar ($)</MenuItem>
                    <MenuItem value="EUR">Euro (€)</MenuItem>
                    <MenuItem value="GBP">British Pound (£)</MenuItem>
                    <MenuItem value="CAD">Canadian Dollar (C$)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </SettingsSection>

          <SettingsSection title="Business Hours" subtitle="Set your operating hours">
            <Grid container spacing={3}>
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                <Grid item xs={12} key={day}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{ minWidth: 100 }}>{day}</Typography>
                    <TextField
                      size="small"
                      type="time"
                      defaultValue="09:00"
                      sx={{ width: 120 }}
                    />
                    <Typography>to</Typography>
                    <TextField
                      size="small"
                      type="time"
                      defaultValue="17:00"
                      sx={{ width: 120 }}
                    />
                    <FormControlLabel
                      control={<Switch defaultChecked={day !== 'Sunday'} />}
                      label="Open"
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </SettingsSection>
        </TabPanel>

        {/* Notification Settings */}
        <TabPanel value={tabValue} index={1}>
          <SettingsSection title="Notification Preferences" subtitle="Choose how you want to receive notifications">
            <List>
              {notificationSettings.map((setting, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={setting.label}
                    secondary={setting.description}
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings[setting.key]}
                      onChange={(e) => handleSettingChange(setting.key, e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </SettingsSection>

          <SettingsSection title="Alert Settings" subtitle="Configure when and how you receive alerts">
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Booking Alert Frequency</InputLabel>
                  <Select defaultValue="immediate">
                    <MenuItem value="immediate">Immediately</MenuItem>
                    <MenuItem value="hourly">Hourly Digest</MenuItem>
                    <MenuItem value="daily">Daily Digest</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Payment Alert Method</InputLabel>
                  <Select defaultValue="email">
                    <MenuItem value="email">Email Only</MenuItem>
                    <MenuItem value="sms">SMS Only</MenuItem>
                    <MenuItem value="both">Email & SMS</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </SettingsSection>
        </TabPanel>

        {/* Security Settings */}
        <TabPanel value={tabValue} index={2}>
          <SettingsSection title="Security Settings" subtitle="Manage your account security preferences">
            <List>
              {securitySettings.map((setting, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={setting.label}
                    secondary={setting.description}
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings[setting.key]}
                      onChange={(e) => handleSettingChange(setting.key, e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </SettingsSection>

          <SettingsSection title="Session Settings" subtitle="Configure session and password policies">
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Session Timeout</InputLabel>
                  <Select
                    value={settings.sessionTimeout}
                    label="Session Timeout"
                    onChange={(e) => handleSettingChange('sessionTimeout', e.target.value)}
                  >
                    <MenuItem value={15}>15 minutes</MenuItem>
                    <MenuItem value={30}>30 minutes</MenuItem>
                    <MenuItem value={60}>1 hour</MenuItem>
                    <MenuItem value={120}>2 hours</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Password Expiry</InputLabel>
                  <Select
                    value={settings.passwordExpiry}
                    label="Password Expiry"
                    onChange={(e) => handleSettingChange('passwordExpiry', e.target.value)}
                  >
                    <MenuItem value={30}>30 days</MenuItem>
                    <MenuItem value={60}>60 days</MenuItem>
                    <MenuItem value={90}>90 days</MenuItem>
                    <MenuItem value={180}>180 days</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </SettingsSection>

          <SettingsSection title="Change Password" subtitle="Update your account password">
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Current Password"
                  type="password"
                  value={changePassword.currentPassword}
                  onChange={(e) => setChangePassword(prev => ({ ...prev, currentPassword: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="New Password"
                  type="password"
                  value={changePassword.newPassword}
                  onChange={(e) => setChangePassword(prev => ({ ...prev, newPassword: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Confirm New Password"
                  type="password"
                  value={changePassword.confirmPassword}
                  onChange={(e) => setChangePassword(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Button variant="contained" sx={{ mt: 1 }}>
                  Update Password
                </Button>
              </Grid>
            </Grid>
          </SettingsSection>
        </TabPanel>

        {/* Payment Settings */}
        <TabPanel value={tabValue} index={3}>
          <SettingsSection title="Payment Methods" subtitle="Configure available payment options">
            <List>
              {paymentSettings.map((setting, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={setting.label}
                    secondary={setting.description}
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings[setting.key]}
                      onChange={(e) => handleSettingChange(setting.key, e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </SettingsSection>

          <SettingsSection title="Payout Settings" subtitle="Configure how and when payouts are processed">
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Payout Schedule</InputLabel>
                  <Select
                    value={settings.payoutSchedule}
                    label="Payout Schedule"
                    onChange={(e) => handleSettingChange('payoutSchedule', e.target.value)}
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="biweekly">Bi-Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Tax Rate (%)"
                  type="number"
                  value={settings.taxRate}
                  onChange={(e) => handleSettingChange('taxRate', e.target.value)}
                  InputProps={{
                    endAdornment: '%',
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </SettingsSection>

          <SettingsSection title="Stripe Configuration" subtitle="Connect your Stripe account for payments">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Chip 
                label={settings.stripeEnabled ? "Connected" : "Not Connected"} 
                color={settings.stripeEnabled ? "success" : "default"}
              />
              <Typography variant="body2" color="text.secondary">
                {settings.stripeEnabled ? "Your Stripe account is connected" : "Connect your Stripe account to accept payments"}
              </Typography>
            </Box>
            <Button variant="outlined">
              {settings.stripeEnabled ? "Manage Stripe Account" : "Connect Stripe"}
            </Button>
          </SettingsSection>
        </TabPanel>

        {/* Team Settings */}
        <TabPanel value={tabValue} index={4}>
          <SettingsSection title="Team Members" subtitle="Manage users who have access to this admin panel">
            <List>
              <ListItem divider>
                <Avatar sx={{ mr: 2 }}>A</Avatar>
                <ListItemText
                  primary="Admin User"
                  secondary="Owner • admin@freshsweeper.com"
                />
                <ListItemSecondaryAction>
                  <Chip label="Owner" color="primary" />
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem divider>
                <Avatar sx={{ mr: 2 }}>M</Avatar>
                <ListItemText
                  primary="Manager User"
                  secondary="Manager • manager@freshsweeper.com"
                />
                <ListItemSecondaryAction>
                  <Chip label="Manager" color="secondary" />
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem>
                <Avatar sx={{ mr: 2 }}>S</Avatar>
                <ListItemText
                  primary="Support User"
                  secondary="Support • support@freshsweeper.com"
                />
                <ListItemSecondaryAction>
                  <Chip label="Support" />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
            <Button variant="contained" sx={{ mt: 2 }}>
              Invite Team Member
            </Button>
          </SettingsSection>

          <SettingsSection title="Role Permissions" subtitle="Configure what each role can access">
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>User Role</InputLabel>
                  <Select defaultValue="manager">
                    <MenuItem value="owner">Owner</MenuItem>
                    <MenuItem value="manager">Manager</MenuItem>
                    <MenuItem value="support">Support</MenuItem>
                    <MenuItem value="cleaner">Cleaner</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <Button variant="outlined" fullWidth sx={{ mt: 1 }}>
                  Manage Permissions
                </Button>
              </Grid>
            </Grid>
          </SettingsSection>
        </TabPanel>
      </Card>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSaveSettings}
          size="large"
        >
          Save Settings
        </Button>
      </Box>
    </Box>
  );
}